import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { notification } from 'antd';
import { Link, useHistory } from 'react-router-dom';
import {
    AlertCircleIcon,
    CameraIcon,
    ChevronsDownUpIcon,
    ChevronsUpDownIcon,
    SettingsIcon,
} from 'lucide-react';

import { campprograms, addcounselor, editcounselor, signupStaffService, staffSignupOtpService } from '../../services/directorServices';
import { getCampDetailService } from '../../services/userServices';
import { initStaffFormSchema } from '../../services/staffFormService';
import { upadteUserProfileAction } from '../../store/user';
import { getBr } from '../../helper';
import { DEFAULT_STAFF_FORM_SCHEMA } from '../../config/staffFormSchema';
import { collectAnswers, getOverallProgress, getSectionProgress } from '../../utils/staffFormCompletion';
import { hydrateValuesFromDetails, mergeSectionVersions } from '../../utils/staffFormValues';
import { useStaffFormAnswers } from './staffApplication/useStaffFormAnswers';
import SectionCard from './staffApplication/SectionCard';
import SectionNav from './staffApplication/SectionNav';
import EmailConfirmationModal from './staffApplication/EmailConfirmationModal';
import useStaffAddOtp from './staffApplication/useStaffAddOtp';
import './staffApplication/staffApplication.generated.css';

// How long before the resend button unlocks. Shorter than the code's own 300s life so an
// applicant whose email never arrives is not stuck for the full five minutes, but long
// enough to spread the endpoint's five-sends-per-hour allowance across a realistic window
// for a delayed email: five sends is four gaps, so 90s covers about six minutes.
const RESEND_COOLDOWN_SECONDS = 90;
const OTP_FALLBACK_TTL_SECONDS = 300;

const EMAIL_PATTERN = /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i;

// The <PhoneInput country="US"> field is US-locked (no country selector in the
// react-phone-number-input/input build) and emits E.164, so a complete number is always
// "+1" + 10 national digits. This is the same "exactly 10 digits" rule the other
// PhoneInput forms enforce (AdvertisingNew, ListCampNew, partner/ContactForm) and the
// staff-form API validators mirror it inline.
const PHONE_PATTERN = /^\+1\d{10}$/;

const AddCounselor = props => {
    const history = useHistory();
    const { values, setValues, setValue, errors, setErrors } = useStaffFormAnswers({});

    // Seeded with the local default so the form never renders empty, then replaced with the
    // camp's stored form once it loads. Read through `sections` below, never directly — an edit
    // renders this merged with the record's own snapshot.
    const [loadedSections, setLoadedSections] = useState(DEFAULT_STAFF_FORM_SCHEMA);
    // The version stamped on `sections` at load time — null until the camp's form has actually
    // loaded. Sent back with the submission so the backend validates and stamps against the
    // exact form this applicant/director rendered, not whatever is current when they hit
    // submit — closing the race where a director publishes a new required question mid-application.
    const [formVersion, setFormVersion] = useState(null);
    const [serverErrors, setServerErrors] = useState([]);
    const [profileImage, setProfileImage] = useState();
    const [profileimageurl, setProfileImageurl] = useState();
    const [counselorfolderId, setCounselorfolderId] = useState([]);
    const [btnloder, setBtnLoader] = useState(false);
    const [campCodeOther, setCampCodeOther] = useState();
    const [programsList, setProgramsList] = useState([]);
    const [campDetail, setCampDetail] = useState({});

    // Public /signup-staff only: the emailed confirmation code that now gates addStaff.
    // otpSentTo is the address the live code was sent to — it is what lets a second submit
    // reuse a code that is still good instead of spending another send.
    const [otpModalVisible, setOtpModalVisible] = useState(false);
    // Director create: separate from the public applicant OTP above.
    const staffOtp = useStaffAddOtp({ ownEmail: props.userProfile?.email });
    const [otp, setOtp] = useState('');
    const [otpError, setOtpError] = useState('');
    const [otpSending, setOtpSending] = useState(false);
    const [otpExpiry, setOtpExpiry] = useState(0);
    const [otpCooldown, setOtpCooldown] = useState(0);
    const [otpMustResend, setOtpMustResend] = useState(false);
    const [otpSentTo, setOtpSentTo] = useState('');
    const otpIntervalRef = useRef(null);

    const [openSections, setOpenSections] = useState([DEFAULT_STAFF_FORM_SCHEMA[0]?.sectionKey]);
    const [activeId, setActiveId] = useState(DEFAULT_STAFF_FORM_SCHEMA[0]?.sectionKey);
    const [showErrors, setShowErrors] = useState(false);

    // init rather than a plain GET: it is public and idempotent, so an applicant arriving on a
    // camp whose director has never opened the Form Builder still gets a stored form to submit
    // against. Falls back to the local default so a bad camp code or a network blip cannot
    // leave a public applicant with nothing to fill in.
    useEffect(() => {
        /*
         * A parent supplying sectionsOverride has already decided which version this edit runs
         * against — the counselor's version-choice modal, which may hand over their original
         * snapshot or a merge of it with the current form. Loading the camp's form here would
         * overwrite that choice with current, so the fetch is skipped entirely rather than
         * raced against.
         */
        if (props.sectionsOverride) {
            setLoadedSections(props.sectionsOverride);
            setFormVersion(props.formVersionOverride ?? null);
            return;
        }
        const campCode = props?.userProfile?.campCode || props?.match?.params?.id;
        if (!campCode) return;
        initStaffFormSchema(campCode)
            .then(({ sections, currentVersion }) => {
                setLoadedSections(sections || DEFAULT_STAFF_FORM_SCHEMA);
                setFormVersion(currentVersion);
            })
            // formVersion is left at its initial null here — buildFormData omits the field
            // entirely for null, which degrades to exactly today's un-versioned submit rather
            // than sending a version nobody rendered.
            .catch(() => setLoadedSections(DEFAULT_STAFF_FORM_SCHEMA));
    }, [props.sectionsOverride, props.formVersionOverride]);

    /*
     * The questions this form actually renders.
     *
     * Editing an existing record shows the camp's current form MERGED with the questions that
     * record was submitted against, so an answer to a question since deleted or hidden is still
     * reachable by whoever is editing. Without this the director could see an answer on the record
     * and then find it missing the moment they clicked Edit — and only the counselor, who has less
     * authority over the record, could correct it.
     *
     * The version sent on submit is unchanged: still the camp's current one. Only the accept side
     * widens, and the backend already allows it — updateCounselor passes the record's stamp in
     * acceptVersions, and the required sweep still reads current alone, so nothing new is demanded.
     *
     * A no-op wherever it should be: adding new personnel has no Details, a record already on the
     * current version merges to an identical set, and the counselor's version-choice modal passes
     * sectionsOverride, having done its own merge with the version the person chose.
     */
    const sections = useMemo(() => {
        const snapshot = props.Details?.staffFormSnapshot?.sections;
        if (props.sectionsOverride || !snapshot) return loadedSections;
        return mergeSectionVersions(loadedSections, snapshot);
    }, [loadedSections, props.Details, props.sectionsOverride]);

    // Same campCode fallback as the two effects either side of this one: the director's own camp,
    // else the camp code in the route (public /signup-staff, and an admin/super-director adding
    // staff for a named camp). Bail when neither resolves rather than sending the literal string
    // "undefined" - the API now reads that as an unknown camp and returns [], but skipping the
    // request is cleaner and matches the initStaffFormSchema effect above. Scoping is enforced
    // server-side: program/campprograms filters to this camp's programs only.
    useEffect(() => {
        const campCode = props?.userProfile?.campCode || props?.match?.params?.id;
        if (!campCode) return;
        campprograms(`/?campCode=${campCode}`).then(res => {
            if (res?.data?.statusCode) {
                setProgramsList(res.data.responseData.result);
            }
        });
    }, []);

    useEffect(() => {
        getCampDetailService(`?campCode=${props?.userProfile?.campCode || props?.match?.params.id}`).then(res => {
            if (res?.data?.statusCode) {
                const result = res.data.responseData.result;
                setCampDetail({
                    agreement: result?.agreement ? getBr(result.agreement) : undefined,
                    trainingGuidePdf: result?.trainingGuidePdf,
                });
            }
        });
    }, [props]);

    useEffect(() => {
        if (props?.match?.params?.id) {
            setCampCodeOther(props.match.params.id);
        }
    }, [props]);

    useEffect(() => {
        if (props.Details !== undefined) {
            setValues(hydrateValuesFromDetails(props.Details));
            setCounselorfolderId(props.Details.counselorfolderId || []);
            setProfileImageurl(props.Details.profileImage);
        }
    }, [props.Details]);

    const upload_image = e => {
        if (e.target.files[0]) {
            setProfileImage(e.target.files[0]);
            const reader = new FileReader();
            reader.addEventListener('load', () => setProfileImageurl(reader.result));
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleFieldChange = (questionKey, value) => {
        if (questionKey === 'email') {
            if (!EMAIL_PATTERN.test(value)) {
                setErrors(prev => ({ ...prev, email: 'Please enter a valid email' }));
                setValue('email', value);
                return;
            }
            setErrors(prev => ({ ...prev, email: undefined }));
        }
        // Phone is optional; flag only a non-empty value that is not a valid number.
        if (questionKey === 'phone') {
            if (value && !PHONE_PATTERN.test(value)) {
                setErrors(prev => ({ ...prev, phone: 'Please enter a valid 10-digit phone number' }));
                setValue('phone', value);
                return;
            }
            setErrors(prev => ({ ...prev, phone: undefined }));
        }
        setValue(questionKey, value);
    };

    const toggleSection = useCallback(sectionKey => {
        setOpenSections(prev => (prev.includes(sectionKey) ? prev.filter(id => id !== sectionKey) : [...prev, sectionKey]));
    }, []);

    const goToSection = useCallback(sectionKey => {
        setOpenSections(prev => (prev.includes(sectionKey) ? prev : [...prev, sectionKey]));
        setActiveId(sectionKey);
        requestAnimationFrame(() => {
            document.getElementById(sectionKey)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                const visible = entries
                    .filter(entry => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
                if (visible) setActiveId(visible.target.id);
            },
            { rootMargin: '-140px 0px -60% 0px', threshold: 0 }
        );
        sections.forEach(section => {
            const el = document.getElementById(section.sectionKey);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [sections]);

    const sectionProgress = useMemo(() => {
        const map = {};
        sections.forEach(section => { map[section.sectionKey] = getSectionProgress(section, values); });
        return map;
    }, [sections, values]);
    const overall = useMemo(() => getOverallProgress(sections, values), [sections, values]);
    const incompleteSections = sections.filter(section => sectionProgress[section.sectionKey].required > 0 && !sectionProgress[section.sectionKey].isComplete);

    // A director who hides every question in a section has removed it from the application;
    // SectionCard filters the questions but would still render the empty shell. Both the nav and
    // the card number their steps from array position, so they have to walk this same list or
    // the numbering skips. The progress helpers stay on the full `sections` — they already
    // ignore disabled questions, so a hidden section contributes zero either way.
    const visibleSections = useMemo(
        () => sections.filter(section => section.questions.some(question => question.enabled)),
        [sections]
    );
    const allOpen = openSections.length === visibleSections.length;

    const context = { campDetail, programsList };

    // One interval drives both counters: the code's own life (from the server's
    // timeRemaining) and the shorter resend lock. Held in a ref and restarted explicitly on
    // every send rather than re-created by an effect, so a resend cannot leave two running.
    const startOtpTimers = seconds => {
        if (otpIntervalRef.current) clearInterval(otpIntervalRef.current);
        setOtpExpiry(seconds);
        setOtpCooldown(Math.min(RESEND_COOLDOWN_SECONDS, seconds));
        otpIntervalRef.current = setInterval(() => {
            setOtpCooldown(prev => (prev > 0 ? prev - 1 : 0));
            setOtpExpiry(prev => {
                if (prev <= 1) {
                    clearInterval(otpIntervalRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => () => clearInterval(otpIntervalRef.current), []);

    // Codes 75 and 20 both mean the code in hand is dead: after three wrong tries even the
    // correct one keeps failing until a new round is started, which is the single most
    // confusing thing about this flow if the copy does not say so.
    const forceResend = message => {
        if (otpIntervalRef.current) clearInterval(otpIntervalRef.current);
        setOtpExpiry(0);
        setOtpCooldown(0);
        setOtpMustResend(true);
        setOtpError(message);
    };

    // Step 1. Calling this again is the resend — the API issues a fresh code and clears the
    // wrong-attempt counter, there is no separate endpoint.
    const requestStaffOtp = isResend => {
        setOtpSending(true);
        setOtpError('');
        const reportFailure = res => {
            const error = res?.data?.error;
            const message = error?.responseMessage || 'We could not send the confirmation code. Please try again.';
            if (isResend) {
                setOtpError(message);
            } else {
                // Nothing is open yet on a first send, so this belongs on the form itself.
                setServerErrors(error?.errors || []);
                notification.open({ description: message });
            }
        };
        return staffSignupOtpService({
            campCode: campCodeOther,
            email: values.email,
            name: `${values.firstName || ''} ${values.lastName || ''}`.trim(),
        }).then(res => {
            setOtpSending(false);
            if (res?.data?.statusCode !== 1) {
                reportFailure(res);
                return false;
            }
            startOtpTimers(res.data.responseData?.timeRemaining || OTP_FALLBACK_TTL_SECONDS);
            setOtpSentTo(values.email);
            setOtpMustResend(false);
            setOtp('');
            return true;
        }).catch(() => {
            setOtpSending(false);
            reportFailure();
            return false;
        });
    };

    // Rebuilt from state on demand rather than held once: the public path posts the same
    // payload a second time alongside the confirmation code, and the applicant may have gone
    // back to fix a field in between. Uploaded PDFs and the profile photo are URLs by now,
    // so nothing here is lost by building it twice.
    const buildFormData = () => {
        const form_data = new FormData();
        form_data.append('name', `${values.firstName || ''} ${values.lastName || ''}`);
        form_data.append('email', values.email);
        if (campCodeOther) {
            form_data.append('campCode', campCodeOther);
            form_data.append('userId', props.match.params.id2);
        } else {
            form_data.append('campCode', props.userProfile.campCode);
        }
        form_data.append('phone', values.phone || '');
        form_data.append('address', values.streetAddress || '');
        form_data.append('dob', values.dob ? values.dob : '');

        if (values.ageGroup !== undefined && String(values.ageGroup).trim() !== '') {
            form_data.append('ageGroup', values.ageGroup);
        }
        if (values.userDescription !== undefined && String(values.userDescription).trim() !== '') {
            form_data.append('userDescription', values.userDescription);
        }
        if (profileImage !== undefined) {
            form_data.append('profileImage', profileImage);
        }
        form_data.append('counselorfolderId', JSON.stringify(counselorfolderId));
        if (values.counselorProgram !== undefined && String(values.counselorProgram).trim() !== '') {
            form_data.append('counselorProgram', values.counselorProgram);
        }
        if (values.userTitle !== undefined) {
            form_data.append('userTitle', values.userTitle);
        }

        const counselorDetails = {
            serving: values.serving,
            firstName: values.firstName,
            lastName: values.lastName,
            streetAddress: values.streetAddress,
            appartment: values.appartment,
            city: values.city,
            zipCode: values.zipCode,
            state: values.state,
            tshirtSize: values.tshirtSize,
            shortsSize: values.shortsSize,
            medicalvalues: values.medicalvalues || [],
            allergyvalues: values.allergyvalues || [],
            allergyDeadly: values.allergyDeadly,
            medicalInsuranceCompany: values.medicalInsuranceCompany,
            policyNumber: values.policyNumber,
            group: values.group,
            primaryPhysician: values.primaryPhysician,
            physicianAddress: values.physicianAddress,
            physicianPhone: values.physicianPhone,
            medicationsTaken: values.medicationsTaken,
            participantBringingOwnMedication: values.participantBringingOwnMedication,
            allowDocumentCamper: values.allowDocumentCamper,
            medicalDocument: values.medicalDocument,
            userDescription: values.userDescription,
            signIsLegal: values.signature?.signed ? 1 : 2,
            readTrainingGuide: values.readTrainingGuide ? 1 : 2,
            signatureImg: values.signature?.image,
        };
        form_data.append('counselorDetails', JSON.stringify(counselorDetails));

        // Additive only — locked questions map to the dedicated fields above; anything
        // non-locked (director-added via the Form Builder) flows through here instead.
        form_data.append('answers', JSON.stringify(collectAnswers(sections, values)));

        // The version of the form actually rendered above. Omitted rather than sent as-is when
        // null: FormData.append coerces null to the literal string "null", not an empty field,
        // and omitting it entirely is what makes the backend fall back to the camp's current
        // version — the same behavior this app had before formVersion existed, so a failed form
        // load degrades gracefully instead of failing the whole submission. Sent unconditionally
        // across all three submit paths (addcounselor, addStaff, editcounselor). editcounselor no
        // longer ignores it: an edit is now validated against the version named here, which is
        // what lets a counselor edit against their original form. A director sends the camp's
        // current version and so is unaffected.
        if (formVersion !== null) {
            form_data.append('formVersion', formVersion);
        }

        // There is no draft-save UI yet (single "Submit application" button), so this is
        // always a real submission. Sent unconditionally across all three submit paths, same
        // as formVersion above: addcounselor/addStaff already resolve to "submitted" when the
        // field is absent, so this is a no-op there. For editcounselor it is load-bearing -
        // that endpoint only stamps staffFormVersion on a call that explicitly carries
        // applicationStatus (see userFacade.js updateCounselor), which was never true before
        // this line existed.
        form_data.append('applicationStatus', 'submitted');
        return form_data;
    };

    // A rejected answer comes back as HTTP 200 with statusCode 0, so success can only be
    // read off statusCode. error.errors names each failing question by its label. Closes the
    // code modal on the way out so the banner it renders is not hidden behind it.
    const handleFailure = res => {
        setBtnLoader(false);
        setOtpModalVisible(false);
        setServerErrors(res?.data?.error?.errors || []);
        notification.open({ description: res?.data?.error?.responseMessage || 'Something went wrong. Please try again.' });
    };

    // Step 2 of the public flow: the form exactly as it is submitted today, plus the code.
    // The API does not consume the code until the applicant row is created, so a failure past
    // verification (email already registered, a missing custom answer) leaves it valid — the
    // applicant fixes the field and handelSubmit reopens the modal with the same code.
    const confirmStaffSignup = () => {
        if (!/^\d{4}$/.test(otp)) {
            setOtpError('Enter the 4-digit code from the email.');
            return;
        }
        setOtpError('');
        setServerErrors([]);
        setBtnLoader(true);

        const form_data = buildFormData();
        form_data.append('otp', otp);

        signupStaffService(form_data).then(res => {
            if (res?.data?.statusCode !== 1) {
                const error = res?.data?.error;
                const message = error?.responseMessage || 'Something went wrong. Please try again.';
                setBtnLoader(false);

                const isOtpFieldError = error?.errorCode === 5
                    && (error?.errors || []).some(fieldError => fieldError.fieldName === 'otp');

                if ([10, 20, 75].includes(error?.errorCode) || isOtpFieldError) {
                    // These messages only ever render inside the modal, so put it back on
                    // screen first: the ✕ still closes while a submit is in flight, and a
                    // wrong code would otherwise fail in total silence.
                    setOtpModalVisible(true);
                    if (error?.errorCode === 75) {
                        // Three wrong tries ends the round outright: the correct code will
                        // keep failing until a new one is issued, which looks broken unless
                        // the copy says so.
                        forceResend('Too many incorrect attempts. That code no longer works — request a new one to continue.');
                        setOtp('');
                    } else if (error?.errorCode === 20) {
                        forceResend('That code has expired. Request a new one to continue.');
                        setOtp('');
                    } else {
                        setOtpError(message);
                        if (error?.errorCode === 10) setOtp('');
                    }
                    return;
                }
                // Everything else is about the application, not the code: close the modal and
                // show it on the form, where the applicant can act on it.
                handleFailure(res);
                return;
            }
            setBtnLoader(false);
            setOtpModalVisible(false);
            history.push('/');
            notification.open({ description: 'Staff application sent to the director successfully' });
        }).catch(() => {
            setBtnLoader(false);
            setOtpModalVisible(true);
            setOtpError('We could not reach the server. Please try again.');
        });
    };

    const handelSubmit = e => {
        e?.preventDefault?.();

        if (incompleteSections.length > 0) {
            setShowErrors(true);
            goToSection(incompleteSections[0].sectionKey);
            return;
        }
        setShowErrors(false);
        setServerErrors([]);
        setBtnLoader(true);

        // Contact-field format gates. These run after the section-completion check, so an
        // empty required phone is already handled; what is left is a value that is present
        // but malformed. Jump back to whichever section holds the field so the inline error
        // is visible rather than swallowed on a section the user has scrolled past.
        const contactSectionKey = sections.find(section =>
            section.questions.some(question => question.questionKey === 'email' || question.questionKey === 'phone')
        )?.sectionKey;
        const failContactValidation = (field, message) => {
            setErrors(prev => ({ ...prev, [field]: message }));
            setShowErrors(true);
            if (contactSectionKey) goToSection(contactSectionKey);
            setBtnLoader(false);
        };

        if (!values.email || String(values.email).trim() === '') {
            failContactValidation('email', 'Email is required');
            return;
        }
        if (!EMAIL_PATTERN.test(String(values.email).trim())) {
            failContactValidation('email', 'Please enter a valid email');
            return;
        }
        if (values.phone && !PHONE_PATTERN.test(values.phone)) {
            failContactValidation('phone', 'Please enter a valid 10-digit phone number');
            return;
        }

        if (props.Details !== undefined) {
            const form_data = buildFormData();
            form_data.append('counselorId', props.Details._id);
            editcounselor(form_data).then(res => {
                if (res?.data?.statusCode !== 1) {
                    handleFailure(res);
                    return;
                }
                // Keep the header greeting in sync only when a user edits their OWN
                // record (/profile/information). On the director's /personnel/:id path
                // `values.firstName/lastName` are the staff member's name, not the
                // director's, so patching userProfile here renamed the logged-in director.
                if (props.Details._id === props.userProfile._id) {
                    props.upadteUserProfileAction({
                        ...props.userProfile,
                        name: `${values.firstName || ''} ${values.lastName || ''}`,
                    });
                }
                props.onSaved && props.onSaved();
                setBtnLoader(false);
                notification.open({ description: 'Staff Updated Successfully' });
            });
        } else if (!campCodeOther) {
            // Director: email the director a code first; the staff is created from confirmDirectorCreate.
            staffOtp.request({ action: 'create', campCode: props.userProfile.campCode, emails: [values.email] })
                .then(() => setBtnLoader(false));
        } else {
            // Public applicant: nothing is posted to addStaff until the code is confirmed.
            // A code already sent to this same address and still alive is reused rather than
            // replaced — the send endpoint allows only five per hour per IP.
            if (otpSentTo === values.email && otpExpiry > 0 && !otpMustResend) {
                setBtnLoader(false);
                setOtpError('');
                setOtpModalVisible(true);
                return;
            }
            requestStaffOtp(false).then(sent => {
                setBtnLoader(false);
                if (sent) setOtpModalVisible(true);
            });
        }
    };

    const confirmDirectorCreate = () => {
        setServerErrors([]);
        staffOtp.submit(code => {
            const form_data = buildFormData();
            form_data.append('otp', code);
            return addcounselor(form_data);
        }).then(res => {
            if (!res) return;
            if (res?.data?.statusCode !== 1) {
                handleFailure(res);
                return;
            }
            history.push('/personnel');
            notification.open({ description: 'Staff Added Successfully' });
        });
    };

    const kicker = props.Details ? 'Edit Personnel' : 'New Personnel';
    const heading = props.Details ? 'Edit Staff Application' : 'Staff Application';
    const cancelTo = props.Details ? `/personnel/${props.Details._id}` : '/personnel';

    return (
        <main className="staff-application-page tw-w-full tw-bg-sand-100 tw-pb-28">
            <div className="tw-border-b tw-border-sand-200 tw-bg-white">
                <div className="tw-mx-auto tw-max-w-7xl tw-px-4 tw-py-8 sm:tw-px-6">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-6">
                        <div className="tw-relative">
                            <img
                                src={profileimageurl ? profileimageurl : require('../../assets/img/camps-user.png')}
                                alt=""
                                className="tw-h-20 tw-w-20 tw-rounded-full tw-bg-brand-50 tw-object-cover tw-ring-4 tw-ring-white" />
                            <label
                                aria-label="Upload profile photo"
                                className="tw-absolute -tw-bottom-1 -tw-right-1 tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-brand-700 tw-text-white tw-shadow-md tw-transition hover:tw-bg-brand-800">
                                <CameraIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                                <input onChange={upload_image} type="file" className="tw-hidden" accept="image/x-png,image/gif,image/jpeg" />
                            </label>
                        </div>
                        <div className="tw-min-w-0 tw-flex-1">
                            <p className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.18em] tw-text-brand-500">{kicker}</p>
                            <h1 className="tw-mt-1 tw-text-2xl tw-font-bold tw-text-gray-900 sm:tw-text-3xl">{heading}</h1>
                            {/* Which version of the camp's form is on screen. Null until the schema
                                loads, and on a camp whose form predates versioning — in both cases
                                the submit falls back to the camp's current version server-side, so
                                there is no number to honestly show. */}
                            {formVersion !== null ? (
                                <p className="tw-mt-1 tw-text-xs tw-text-gray-500">Form version {formVersion}</p>
                            ) : null}
                        </div>
                        {/* Directors only. `campCodeOther` alone is not enough: it hides this from the
                            public applicant, but this same component renders a counselor's own record on
                            /profile/information, where they are signed in and campCodeOther is unset. The
                            builder's mutation routes are behind autntctTknAdmnDir (UserType 10/2/11), so a
                            counselor following this link reaches a page where every action fails. Same test
                            as Counselors.js's showFormBuilder. */}
                        {!campCodeOther && props.userProfile?.UserType === 2 ? (
                            <Link
                                to="/personnel/form-builder"
                                className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-sand-300 tw-px-3.5 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-gray-700 tw-transition hover:tw-border-brand-300 hover:tw-text-brand-700">
                                <SettingsIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                                Manage questions
                            </Link>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className="tw-mx-auto tw-max-w-7xl tw-px-4 tw-py-8 sm:tw-px-6">
                {serverErrors.length > 0 ? (
                    <div role="alert" className="tw-mb-6 tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-border-brand-200 tw-bg-brand-50 tw-p-4">
                        <AlertCircleIcon className="tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-text-brand-600" aria-hidden="true" />
                        <div className="tw-min-w-0">
                            <p className="tw-text-sm tw-font-semibold tw-text-brand-900">Your application could not be saved</p>
                            <ul className="tw-mt-1.5 tw-list-disc tw-pl-4 tw-text-xs tw-text-brand-800">
                                {serverErrors.map(fieldError => <li key={fieldError.fieldName}>{fieldError.message}</li>)}
                            </ul>
                        </div>
                    </div>
                ) : null}

                {showErrors && incompleteSections.length > 0 ? (
                    <div role="alert" className="tw-mb-6 tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-border-brand-200 tw-bg-brand-50 tw-p-4">
                        <AlertCircleIcon className="tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-text-brand-600" aria-hidden="true" />
                        <div>
                            <p className="tw-text-sm tw-font-semibold tw-text-brand-900">
                                {incompleteSections.length} section{incompleteSections.length > 1 ? 's' : ''} still need attention
                            </p>
                            <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-2">
                                {incompleteSections.map(section => (
                                    <button
                                        key={section.sectionKey}
                                        type="button"
                                        onClick={() => goToSection(section.sectionKey)}
                                        className="tw-rounded-full tw-bg-white tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-brand-700 tw-ring-1 tw-ring-brand-200 tw-transition hover:tw-bg-brand-100">
                                        {section.title}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-[280px_minmax(0,1fr)]">
                    <div>
                        <SectionNav sections={visibleSections} values={values} activeId={activeId} onSelect={goToSection} />
                    </div>

                    <form onSubmit={handelSubmit} noValidate encType="multipart/form-data">
                        <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between">
                            <p className="tw-text-xs tw-text-gray-500">
                                Fields marked <span className="tw-font-semibold tw-text-brand-600">*</span> are required.
                            </p>
                            <button
                                type="button"
                                onClick={() => setOpenSections(allOpen ? [] : visibleSections.map(section => section.sectionKey))}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-brand-700 tw-transition hover:tw-text-brand-900">
                                {allOpen ? <ChevronsDownUpIcon className="tw-h-4 tw-w-4" aria-hidden="true" /> : <ChevronsUpDownIcon className="tw-h-4 tw-w-4" aria-hidden="true" />}
                                {allOpen ? 'Collapse all' : 'Expand all'}
                            </button>
                        </div>

                        <div className="tw-space-y-4">
                            {visibleSections.map((section, index) => (
                                <SectionCard
                                    key={section.sectionKey}
                                    index={index}
                                    section={section}
                                    values={values}
                                    errors={errors}
                                    progress={sectionProgress[section.sectionKey]}
                                    open={openSections.includes(section.sectionKey)}
                                    onToggle={() => toggleSection(section.sectionKey)}
                                    onChange={handleFieldChange}
                                    context={context} />
                            ))}
                        </div>
                    </form>
                </div>
            </div>

            <div className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-30 tw-border-t tw-border-sand-200 tw-bg-white/95 tw-backdrop-blur">
                <div className="tw-mx-auto tw-flex tw-max-w-7xl tw-flex-wrap tw-items-center tw-gap-3 tw-px-4 tw-py-3 sm:tw-px-6">
                    <p className="tw-text-xs tw-text-gray-500">
                        <span className="tw-font-semibold tw-text-gray-800">{overall.completed}/{overall.required}</span> required fields complete
                    </p>
                    <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
                        {/*
                            A parent that supplies HideEditmode owns this form's lifecycle and
                            renders it inline, so cancelling is a mode change, not navigation.
                            Keying off HideEditmode rather than Details matters: cancelTo points at
                            /personnel/:id, which is the director's own page - harmless there, but
                            it used to send a counselor cancelling on /profile/information into the
                            director's detail screen for their own record, permissions toggles and
                            all. Only the standalone /addcounselor and public signup entries, which
                            have no parent to return to, still navigate.
                        */}
                        {props.HideEditmode ? (
                            <button
                                type="button"
                                onClick={() => props.HideEditmode()}
                                className="tw-rounded-lg tw-border tw-border-sand-300 tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-gray-600 tw-transition hover:tw-bg-sand-100">
                                Cancel
                            </button>
                        ) : (
                            <Link
                                to={cancelTo}
                                className="tw-rounded-lg tw-border tw-border-sand-300 tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-gray-600 tw-transition hover:tw-bg-sand-100">
                                Cancel
                            </Link>
                        )}
                        <button
                            type="button"
                            onClick={handelSubmit}
                            disabled={btnloder}
                            className="tw-rounded-lg tw-bg-brand-700 tw-px-5 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-transition hover:tw-bg-brand-800 disabled:tw-opacity-60">
                            {btnloder ? 'Saving…' : 'Submit application'}
                        </button>
                    </div>
                </div>
            </div>

            {campCodeOther && otpModalVisible ? (
                <EmailConfirmationModal
                    email={otpSentTo || values.email}
                    otp={otp}
                    onOtpChange={value => { setOtp(value); setOtpError(''); }}
                    error={otpError}
                    expirySeconds={otpExpiry}
                    cooldownSeconds={otpCooldown}
                    mustResend={otpMustResend}
                    sending={otpSending}
                    submitting={btnloder}
                    onResend={() => requestStaffOtp(true)}
                    onConfirm={confirmStaffSignup}
                    onClose={() => setOtpModalVisible(false)} />
            ) : null}

            {!campCodeOther && staffOtp.visible ? (
                <EmailConfirmationModal
                    {...staffOtp.modalProps}
                    description="Enter it to add this staff member."
                    onConfirm={confirmDirectorCreate} />
            ) : null}
        </main>
    );
};

const mapStateToProps = state => {
    const { userProfile } = state.user;
    const { preList } = state.program;
    return { userProfile, preList };
};

const mapDispatchToProps = dispatch => ({
    upadteUserProfileAction: payload => dispatch(upadteUserProfileAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddCounselor);
